import { useCallback, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { RootState } from "@/components/state/store";
import { setBackgroundTask } from "@/components/state/backgroundTasks/backgroundTasksSlice";
import { startBackgroundTask } from "@/components/backgroundTasks/startBackgroundTask";
import {
  forgetRunningWorkerCsvImport,
  getRunningWorkerCsvImport,
  rememberRunningWorkerCsvImport,
} from "@/components/userAdministration/runningWorkerCsvImportStorage";

export interface RunningWorkerCsvImport {
  taskId: string;
  startedAt?: string;
}

interface Params {
  companySubjectId?: string;
  enabled: boolean;
  onImportFinished: () => void;
}

export default function useWorkerCsvImportTracking({
  companySubjectId,
  enabled,
  onImportFinished,
}: Params) {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  // kept in a ref so a new t() instance (language switch, test mocks) does not re-run the effects below
  const taskDescriptionRef = useRef("");
  taskDescriptionRef.current = t("worker_csv_import_taskstatus");

  const runningTask = useSelector((state: RootState) =>
    enabled && companySubjectId
      ? state.backgroundTasks.tasks.find(
          (task) =>
            task.kind === "worker_csv" &&
            task.companySubjectId === companySubjectId &&
            task.status === "in_progress"
        )
      : undefined
  );

  const trackImport = useCallback(
    (taskId: string) => {
      if (!enabled || !companySubjectId) return;
      const startedAt = new Date().toISOString();
      rememberRunningWorkerCsvImport(companySubjectId, { taskId, startedAt });
      startBackgroundTask(dispatch, {
        id: taskId,
        description: taskDescriptionRef.current,
        kind: "worker_csv",
        companySubjectId,
        startedAt,
      });
    },
    [enabled, companySubjectId, dispatch]
  );

  // An import started before a reload is picked up again from local storage.
  useEffect(() => {
    if (!enabled || !companySubjectId) return;
    const storedImport = getRunningWorkerCsvImport(companySubjectId);
    if (!storedImport) return;
    dispatch(
      setBackgroundTask({
        id: storedImport.taskId,
        description: taskDescriptionRef.current,
        kind: "worker_csv",
        companySubjectId,
        startedAt: storedImport.startedAt,
      })
    );
  }, [enabled, companySubjectId, dispatch]);

  const trackedImportRef = useRef<{
    taskId: string;
    companySubjectId: string;
  } | null>(null);

  useEffect(() => {
    if (runningTask && companySubjectId) {
      trackedImportRef.current = { taskId: runningTask.id, companySubjectId };
      return;
    }
    const trackedImport = trackedImportRef.current;
    if (!trackedImport) return;
    trackedImportRef.current = null;
    if (trackedImport.companySubjectId !== companySubjectId) return;
    forgetRunningWorkerCsvImport(trackedImport.companySubjectId);
    onImportFinished();
  }, [runningTask, companySubjectId, onImportFinished]);

  const runningImport: RunningWorkerCsvImport | null = runningTask
    ? { taskId: runningTask.id, startedAt: runningTask.startedAt }
    : null;

  return { runningImport, trackImport };
}
